import React, { useState, useCallback } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import AlternateSizeGuide from '~/shared/components/AlternateSizeGuide';
import CloseButton from '~/shared/components/CloseButton';
import FableticsModal from '~/shared/components/FableticsModal';
import scrollToDomElement from '~/shared/utils/scrollToDomElement';
import { AssetContainer } from '~/techstyle-shared/react-assets';
import {
  Button,
  mobile,
  desktop,
  useBreakpoint,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import {
  useIntl,
  FormattedMessage,
  FormattedHTMLMessage,
} from '~/techstyle-shared/react-intl';

const dialogStyle = css`
  ${desktop`
    max-height: calc(100vh - 128px);
  `}
`;

const ModalCloseButton = styled(CloseButton)`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 18px;
`;

const ImageWrapper = styled.div`
  max-width: 800px;
  ${mobile`
    margin: 52px 0;
  `}
`;

const modalContentWrapperStyles = css`
  display: unset;
`;

const LinkButton = styled(Button).attrs({
  variant: 'unstyled',
})`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: ${0.2 / 12}em;
  text-decoration: underline;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

const SizeGuideButton = styled(Button).attrs({
  variant: 'unstyled',
})`
  color: ${({ theme }) => theme.colors.scarcityCallout};
  font-weight: 700;
  font-size: 14px;
  line-height: 2;
  letter-spacing: ${-0.35 / 14}em;
  text-decoration: underline;

  ${({ sizeGuideButtonStyle }) => sizeGuideButtonStyle}
`;

const ScrubSizeStockMessage = styled.div`
  display: inline;
  & > span {
    white-space: normal;
  }
`;

const ScrubSizeGuideMessage = styled.div`
  display: inline;
  margin-bottom: 8px;
`;

const ScrubSizeMessageWrapper = styled.div`
  font-size: 12px;
  line-height: ${16 / 12};
  letter-spacing: ${0.2 / 12};
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.scrubsSizeMessageBorder};
  margin-bottom: 31px;
  ${mobile`
    margin-bottom: 18px;
  `}
`;

const StorytellingLink = styled.div`
  color: ${({ theme }) => theme.colors.scarcityCallout};
  font-weight: 700;
  line-height: ${20 / 14};
  font-size: 14px;
  text-decoration: underline;
  margin-top: 7px;
`;

const transitionProps = {
  enter: { opacity: 1, transform: 'scale(1, 1)' },
  from: { opacity: 0, transform: 'scale(0.5, 0.5)' },
  leave: { opacity: 0, transform: 'scale(0.5, 0.5)' },
};

const womenScrubsTopIds = config.get(
  'public.productCategories.scrubsWomensTop'
);
const womenScrubsBottomId = config.get(
  'public.productCategories.scrubsWomensBottom'
);
const womenUnderscrubsId = config.get(
  'public.productCategories.scrubsWomensUnderscrubs'
);
const womenScrubsJacketId = config.get(
  'public.productCategories.scrubsWomensJacket'
);
const womenScrubsCoatId = config.get(
  'public.productCategories.scrubsWomensCoat'
);
const womenScrubsJumpsuit = config.get(
  'public.productCategories.scrubsWomensJumpsuit'
);
const menScrubBottomsId = config.get(
  'public.productCategories.scrubsMensBottom'
);
const mensScrubTopsId = config.get('public.productCategories.scrubsMensTop');
const mensScrubJacketId = config.get(
  'public.productCategories.scrubsMensJacket'
);
const mensUnderscrubsId = config.get(
  'public.productCategories.scrubsMensUnderscrubs'
);
const mensScrubsCoatId = config.get('public.productCategories.scrubsMensCoat');
const scrubsShoesId = config.get('public.productCategories.scrubsShoes');

const MenScrubSizeGuide = () => {
  const { isMobile } = useBreakpoint();

  // mens scrub size guide asset
  const assetContainer = isMobile
    ? 'scrubs_m_sizeguide_8949_mobile'
    : 'scrubs_m_sizeguide_8949_desktop';

  return (
    <ImageWrapper>
      <AssetContainer name={assetContainer} />
    </ImageWrapper>
  );
};

const ScrubsShoesSizeGuide = () => {
  const { isMobile } = useBreakpoint();
  const assetContainer = isMobile
    ? 'scrubs_m_sizeguide_mobile'
    : 'scrubs_m_sizeguide_desktop';

  return (
    <ImageWrapper>
      <AssetContainer name={assetContainer} />
    </ImageWrapper>
  );
};

const ScrubsSizeGuideModal = ({
  categoryIdList,
  showMessaging = false,
  showSizeCalloutMessage,
  sizeGuideButtonStyle,
  useAlternate = false,
}) => {
  const intl = useIntl();
  const closeLabel = intl.formatMessage({
    id: 'global_cta.close',
    defaultMessage: 'Close',
  });
  const { isMobile } = useBreakpoint();
  const [isModalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);
  const { isBundle } = useProductDetailContext();

  const isScrubsShoes = categoryIdList?.includes(scrubsShoesId);
  const isMenScrubBottom = categoryIdList?.includes(menScrubBottomsId);
  const isMenScrubJacket = categoryIdList?.includes(mensScrubJacketId);
  const isMenScrubTop = categoryIdList?.includes(mensScrubTopsId);
  const isMenScrubUnderscrub = categoryIdList?.includes(mensUnderscrubsId);
  const isMenScrubCoat = categoryIdList?.includes(mensScrubsCoatId);
  const isMenScrubs =
    isMenScrubBottom ||
    isMenScrubJacket ||
    isMenScrubTop ||
    isMenScrubUnderscrub ||
    isMenScrubCoat;

  const isWomensTop = categoryIdList?.includes(womenScrubsTopIds);
  const isWomensBottom = categoryIdList?.includes(womenScrubsBottomId);
  const isWomensUnderscrubOrJacketOrJumpsuit =
    categoryIdList?.includes(womenUnderscrubsId) ||
    categoryIdList?.includes(womenScrubsJacketId) ||
    categoryIdList?.includes(womenScrubsCoatId) ||
    categoryIdList?.includes(womenScrubsJumpsuit);
  const isWomensUnderscrub = categoryIdList?.includes(womenUnderscrubsId);
  const isWomensJacket = categoryIdList?.includes(womenScrubsJacketId);

  // women scrubs size guide asset
  const womenTopAssetContainer = isMobile
    ? 'scrubs_w_sizeguide_8946_mobile'
    : 'scrubs_w_sizeguide_8946_desktop';

  const womenBottomAssetContainer = isMobile
    ? 'scrubs_w_sizeguide_8947_mobile'
    : 'scrubs_w_sizeguide_8947_desktop';

  const womenJacketUnderscrubJumpsuitAssetContainer = isMobile
    ? 'scrubs_w_sizeguide_8948_mobile'
    : 'scrubs_w_sizeguide_8948_desktop';

  return (
    <>
      {showSizeCalloutMessage && (
        <SizeGuideButton
          variant="unstyled"
          onClick={openModal}
          sizeGuideButtonStyle={sizeGuideButtonStyle}
        >
          <FormattedHTMLMessage
            id="scrubs_pdp_size.fit_callout"
            defaultMessage=" "
          />
        </SizeGuideButton>
      )}
      {showMessaging && (
        <ScrubSizeMessageWrapper>
          {isWomensTop && (
            <>
              <ScrubSizeStockMessage>
                <FormattedHTMLMessage
                  id="scrubs_pdp_size.womens_scrubs_tops_stock"
                  defaultMessage=" "
                />
              </ScrubSizeStockMessage>
              <ScrubSizeGuideMessage>
                <FormattedHTMLMessage
                  id="scrubs_pdp_size.womens_scrubs_tops_size"
                  defaultMessage=" "
                  values={{
                    break: <br />,
                    sizeGuideLink: (
                      <LinkButton onClick={openModal}>
                        <FormattedMessage
                          id="scrubs_pdp_size.scrubs_size_guide"
                          defaultMessage="Scrubs Size Guide"
                        />
                      </LinkButton>
                    ),
                  }}
                />
              </ScrubSizeGuideMessage>
            </>
          )}
          {isWomensBottom && (
            <>
              <ScrubSizeStockMessage>
                <FormattedHTMLMessage
                  id="scrubs_pdp_size.womens_scrubs_bottoms_stock"
                  defaultMessage=" "
                />
              </ScrubSizeStockMessage>
              <ScrubSizeGuideMessage>
                <FormattedHTMLMessage
                  id="scrubs_pdp_size.womens_scrubs_bottoms_size"
                  defaultMessage=" "
                  values={{
                    break: <br />,
                    sizeGuideLink: (
                      <LinkButton onClick={openModal}>
                        <FormattedMessage
                          id="scrubs_pdp_size.scrubs_size_guide"
                          defaultMessage="Scrubs Size Guide"
                        />
                      </LinkButton>
                    ),
                  }}
                />
              </ScrubSizeGuideMessage>
            </>
          )}
          {isWomensUnderscrub && (
            <>
              <ScrubSizeStockMessage>
                <FormattedHTMLMessage
                  id="scrubs_pdp_size.womens_scrubs_underscrubs_stock"
                  defaultMessage=" "
                />
              </ScrubSizeStockMessage>
              <ScrubSizeGuideMessage>
                <FormattedHTMLMessage
                  id="scrubs_pdp_size.womens_scrubs_underscrubs_size"
                  defaultMessage=" "
                  values={{
                    break: <br />,
                    sizeGuideLink: (
                      <LinkButton onClick={openModal}>
                        <FormattedMessage
                          id="scrubs_pdp_size.scrubs_size_guide"
                          defaultMessage="Scrubs Size Guide"
                        />
                      </LinkButton>
                    ),
                  }}
                />
              </ScrubSizeGuideMessage>
            </>
          )}
          {isWomensJacket && (
            <>
              <ScrubSizeStockMessage>
                <FormattedHTMLMessage
                  id="scrubs_pdp_size.womens_scrubs_jackets_stock"
                  defaultMessage=" "
                />
              </ScrubSizeStockMessage>
              <ScrubSizeGuideMessage>
                <FormattedHTMLMessage
                  id="scrubs_pdp_size.womens_scrubs_jackets_size"
                  defaultMessage=" "
                  values={{
                    break: <br />,
                    sizeGuideLink: (
                      <LinkButton onClick={openModal}>
                        <FormattedMessage
                          id="scrubs_pdp_size.scrubs_size_guide"
                          defaultMessage="Scrubs Size Guide"
                        />
                      </LinkButton>
                    ),
                  }}
                />
              </ScrubSizeGuideMessage>
            </>
          )}
          {isMenScrubs && (
            <ScrubSizeStockMessage>
              <FormattedHTMLMessage
                id="scrubs_pdp_size.mens_scrubs_all_size"
                defaultMessage=" "
              />
            </ScrubSizeStockMessage>
          )}
          {!isBundle && (
            <StorytellingLink>
              <LinkButton
                onClick={() => {
                  scrollToDomElement(
                    document.querySelector('[data-ref=find_your_fit]') ||
                      document.querySelector(
                        '[data-builder-component-type=find_your_fit_unit]'
                      )
                  );
                }}
              >
                <FormattedHTMLMessage
                  id="scrubs_pdp_size.size_popup_details"
                  defaultMessage=" "
                />
              </LinkButton>
            </StorytellingLink>
          )}
        </ScrubSizeMessageWrapper>
      )}
      <FableticsModal
        contentWrapperStyles={modalContentWrapperStyles}
        dialogStyle={dialogStyle}
        isOpen={isModalOpen}
        onExit={closeModal}
        closeButton={<ModalCloseButton aria-label={closeLabel} />}
        showCloseButton
        transitionEnter={transitionProps.enter}
        transitionFrom={transitionProps.from}
        transitionLeave={transitionProps.leave}
        title={intl.formatMessage({
          id: 'scrubs_pdp_size.scrubs_size_guide',
          defaultMessage: 'Scrubs Size Guide',
        })}
        variant="unpadded"
      >
        {useAlternate ? (
          <AlternateSizeGuide isScrubs />
        ) : (
          <>
            {isScrubsShoes && <ScrubsShoesSizeGuide />}
            {!isMenScrubs ? (
              <ImageWrapper>
                {isWomensTop ? (
                  <AssetContainer
                    name={womenTopAssetContainer}
                    aspectRatio="auto"
                  />
                ) : null}
                {isWomensBottom ? (
                  <AssetContainer
                    name={womenBottomAssetContainer}
                    aspectRatio="auto"
                  />
                ) : null}
                {isWomensUnderscrubOrJacketOrJumpsuit ? (
                  <AssetContainer
                    name={womenJacketUnderscrubJumpsuitAssetContainer}
                    aspectRatio="auto"
                  />
                ) : null}
              </ImageWrapper>
            ) : (
              <MenScrubSizeGuide />
            )}
          </>
        )}
      </FableticsModal>
    </>
  );
};

ScrubsSizeGuideModal.propTypes = {
  categoryIdList: PropTypes.array,
  showMessaging: PropTypes.bool,
  showSizeCalloutMessage: PropTypes.bool,
  sizeGuideButtonStyle: PropTypes.any,
  useAlternate: PropTypes.bool, // use the alternate size guide for this product
};

export default ScrubsSizeGuideModal;
